import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { StateService } from '@uirouter/core';

import * as _ from 'lodash';

import { ModalService } from 'src/app/components/modals/modal.service';
import {
  ProcessErrorCodeAdapterService,
  ProcessErrorCodeFn,
} from 'src/app/components/scrolling-list/services/process-error-code-adapter.service';
import {
  ScrollingListFactoryService,
  ScrollingListInstance,
} from 'src/app/components/scrolling-list/services/scrolling-list-factory.service';

import { ScheduleApiService } from 'src/app/api/services/schedule-api.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { SCHEDULE_ACCESS } from 'src/app/auth/types/access-level';
import { PlaylistService } from './playlist.service';

import { Schedule } from 'src/app/api/services/schedule';
import {
  ItemSelectorDialogComponent,
  ItemSelectorService,
} from 'src/app/components/folders/item-selector-dialog/item-selector-dialog.component';
import { CompanyAssetsService } from 'src/app/shared/services/company-assets.service';
import { AddToScheduleModalComponent } from '../components/add-to-schedule-modal/add-to-schedule-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ScheduleSelectorService implements ItemSelectorService<Schedule> {
  addItem(): void {
    this.addSchedule();
  }

  getScrollingList(): ScrollingListInstance {
    return this.unselectedSchedules as ScrollingListInstance;
  }

  getSelectedItems(): Schedule[] {
    return this.selectedSchedules;
  }

  search: any = {
    sortBy: 'name',
  };
  selectedSchedules = [];
  unselectedSchedules: ScrollingListInstance | null = null;
  selectedCount = 0;

  presentation;

  hasSelectedSchedules = false;
  loadingSchedules = false;

  errorMessage;
  apiError;

  schedulesComponent = {
    type: 'rise-schedules',
    service: this,
    showNoSchedulesError: false,
  };

  private processErrorCode: ProcessErrorCodeFn;

  constructor(
    private stateService: StateService,
    processErrorCodeAdapter: ProcessErrorCodeAdapterService,
    private modalService: ModalService,
    private scrollingListFactory: ScrollingListFactoryService,
    private scheduleApiService: ScheduleApiService,
    private playlistService: PlaylistService,
    private companyAssetsService: CompanyAssetsService,
    private permissionService: PermissionService,
    private matDialog: MatDialog,
  ) {
    this.processErrorCode = processErrorCodeAdapter.createCallable();
  }

  _reset() {
    // reset search query
    this.search = {
      sortBy: 'name',
    };
    this.selectedCount = 0;
  }

  loadSelectedSchedules() {
    if (!this.presentation) {
      return Promise.resolve();
    }

    var search = {
      sortBy: 'name',
      filter: 'presentationIds:~\"' + this.presentation?.id + '\"',
    };

    this.hasSelectedSchedules = true;

    this.selectedSchedules = [];
    this.loadingSchedules = true;

    return this.scheduleApiService
      .list(search)
      .then((result) => {
        this.selectedSchedules = result.items
          ? result.items.map((item) => ({
              ...item,
              isSelected: true,
            }))
          : [];

        this.hasSelectedSchedules = !!this.selectedSchedules.length;
      })
      .catch((e) => {
        this.errorMessage = 'Failed to load schedules.';
        this.apiError = this.processErrorCode('Schedules', 'load', e);

        console.error(this.errorMessage, e);
      })
      .finally(() => {
        this.loadingSchedules = false;
      });
  }

  getSchedulesComponent(currentPresentation) {
    this.presentation = currentPresentation;
    this.schedulesComponent.showNoSchedulesError = false;

    this.loadSelectedSchedules();

    return this.schedulesComponent;
  }

  _loadUnselectedSchedules() {
    if (this.presentation) {
      this.search.filter =
        'NOT presentationIds:~\"' + this.presentation.id + '\"';
    }

    this.unselectedSchedules = this.scrollingListFactory.create(
      this.scheduleApiService.list.bind(this.scheduleApiService),
      this.search,
    );
  }

  load(search?: any) {
    this._reset();

    if (search) Object.assign(this.search, search);
    this._loadUnselectedSchedules();
  }

  _getSelectedScheduleIds() {
    var filteredSchedules = _.filter(
      this.unselectedSchedules.items.list,
      (item) => {
        return item.isSelected;
      },
    );

    return _.map(filteredSchedules, (item) => {
      return item.id;
    });
  }

  _getUnselectedScheduleIds() {
    var filteredSchedules = _.filter(this.selectedSchedules, (item) => {
      return item.isSelected === false;
    });

    return _.map(filteredSchedules, (item) => {
      return item.id;
    });
  }

  _updateSelectedCount() {
    this.selectedCount =
      this._getSelectedScheduleIds().length +
      this._getUnselectedScheduleIds().length;
  }

  selectItem(item, inSelectedSchedules?) {
    if (item.isSelected === true) {
      item.isSelected = false;
    } else if (item.isSelected === false) {
      item.isSelected = true;
    } else {
      item.isSelected = !inSelectedSchedules;
    }

    this._updateSelectedCount();
  }

  isSelected(item, inSelectedSchedules?) {
    if (item.isSelected === true) {
      return true;
    } else if (item.isSelected === false) {
      return false;
    } else {
      return inSelectedSchedules;
    }
  }

  _updateSelectedSchedules() {
    var scheduleIds = this._getSelectedScheduleIds();

    if (!scheduleIds.length) {
      return Promise.resolve();
    }

    var playlistItem = this.playlistService.newPresentationItem(
      this.presentation,
    );

    return this.playlistService
      .initPlayUntilDone(playlistItem, this.presentation, true)
      .then(() => {
        return this.scheduleApiService.addPresentation(
          scheduleIds,
          JSON.stringify(playlistItem),
        );
      });
  }

  _updateUnselectedSchedules() {
    var scheduleIds = this._getUnselectedScheduleIds();

    if (!scheduleIds.length) {
      return Promise.resolve();
    }

    return this.scheduleApiService.removePresentation(
      scheduleIds,
      this.presentation.id,
    );
  }

  select() {
    if (this.selectedCount === 0) {
      return;
    }

    this.loadingSchedules = true;

    return Promise.all([
      this._updateSelectedSchedules(),
      this._updateUnselectedSchedules(),
    ]).then(() => {
      return this.loadSelectedSchedules();
    });
  }

  openDialog(): void {
    const dialogRef = this.matDialog.open(
      ItemSelectorDialogComponent<Schedule>,
      {
        data: {
          kind: 'Schedule',
          multiple: true,
          canAdd: this.permissionService.hasAccess(SCHEDULE_ACCESS.FULL_ACCESS),
        },
        ariaLabelledBy: 'title',
        ariaModal: true,
        width: '500px',
        maxWidth: 'calc(100vw - 32px)',
      },
    );
    dialogRef.componentInstance.itemSelectorService = this;
    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action === 'add') {
        this.addSchedule();
      } else if (result?.action === 'edit') {
        this.editSchedule(result.item);
      } else if (result) {
        this.select();
      }
    });
  }

  addSchedule() {
    if (!this.permissionService.hasAccess(SCHEDULE_ACCESS.FULL_ACCESS)) {
      return;
    }

    this.stateService.go('apps.schedules.add', {
      presentationItem: this.presentation,
    });
  }

  editSchedule(schedule: any) {
    this.stateService.go('apps.schedules.details', {
      scheduleId: schedule.id,
    });
  }

  checkAssignedToSchedules() {
    return this.companyAssetsService.hasSchedules().then((hasSchedules) => {
      if (hasSchedules && !this.hasSelectedSchedules) {
        return this._showAddToScheduleModal();
      }
    });
  }

  _showAddToScheduleModal() {
    this.schedulesComponent.showNoSchedulesError = true;
    return this.modalService.showDialog(AddToScheduleModalComponent, null, {
      width: this.modalService.MODAL_SM,
    });
  }
}
